import { Alert, Button, Layout, Space } from 'antd';
import { notification } from '@/components/Notification';
import { message } from '@/components/Message';
import React, {
  ReactNode,
  Reducer,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import api from '../../api';
import {
  addWsListener,
  CommonTextTipMsg,
  DOMAIN_INTERACT_OUTSIDE,
  DOMAIN_POS,
  removeWsListener,
  useWsConnection,
  WS_CLOSE_REASON,
  WsActions,
} from '../../api/ws';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { PosUIConfig } from '@/models/pos';
import Login from '../../pages/Login';
import { DEFAULT_POS_UI_CONFIG } from '../../pages/POS/Pages/System/UI';
import { assertUnreachable } from '@/utils/common';
import { openCashDraw } from '@/utils/printer';
import { loadSoundResources, playSound } from '@/utils/sound';
import { STORAGE_KEY_POS_UI_CONFIG } from '@/utils/storage';
import Modal from '../Modal';
import PosButton from '../PosButton';
import {
  bindGotoLauncher,
  bindGotoOrder,
  bindGotoSetting,
  bindGotoUser,
  bindGotoWorkbench,
  bindOpenCashDraw,
  bindReload,
} from './globalHotkeys';
import LayoutPosHeader from './Header';
import styles from './style.less';
import Synchronizer from './Synchronizer';
import KickAwayModal from './Synchronizer/kickAwayModal';

const { Content } = Layout;

type LayoutProps = {
  children: ReactNode;
};

type POSConfig = {
  uiConfig: PosUIConfig;
};

type POSConfigActions = {
  type: 'UPDATE_UI';
  payload: PosUIConfig;
};

export const POSConfigContext = React.createContext<{
  config: POSConfig;
  updateConfig: React.Dispatch<POSConfigActions>;
}>(null);

enum RequireLoginMode {
  Never,
  Required,
  RequiredAdminChanged,
}

const LayoutPos: React.FC<LayoutProps> = ({ children }: LayoutProps): React.ReactElement => {
  const navigate = useNavigate();
  const heartbeatInterval = useRef<NodeJS.Timeout>();
  const [{ userName }, , helpers] = useContext(BaseInfoContext);
  const [requireLogin, setRequireLogin] = useState<RequireLoginMode>(RequireLoginMode.Never);
  const [notificationNode, setNotificationNode] = useState<ReactNode>(null);
  // notification key 记录，防止重复弹出
  const notificationKeys = useRef<Set<string>>(new Set());
  const [posUIConfig] = useLocalStorage<PosUIConfig>(
    STORAGE_KEY_POS_UI_CONFIG,
    DEFAULT_POS_UI_CONFIG
  );

  // ws 连接
  const [kickAwayReason, setKickAwayReason] = useState<WS_CLOSE_REASON>();
  const { terminate: terminateWsConnection, reconnect: restartWsConnection } = useWsConnection({
    onKickAway: reason => {
      setKickAwayReason(reason);
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    },
  });

  // 加载声音资源
  useEffect(() => {
    loadSoundResources();
    document.body.classList.add('posContainer');
  }, []);

  // pos config reducer
  const [posConfig, updatePosConfig] = useReducer<Reducer<POSConfig, POSConfigActions>>(
    useCallback((state, action) => {
      switch (action.type) {
        case 'UPDATE_UI':
          return {
            ...state,
            uiConfig: { ...action.payload },
          };
      }
      return assertUnreachable(action.type);
    }, []),
    {
      uiConfig: posUIConfig,
    }
  );

  useEffect(() => {
    const id = addWsListener<CommonTextTipMsg>(DOMAIN_POS, WsActions.COMMON_TEXT_TIP, data => {
      notification[data.level]({
        duration: null,
        message: `远程通知`,
        description: data.text,
        key: data.key,
        actions: (
          <>
            <PosButton
              type={'primary'}
              onClick={() => {
                notification.destroy(data.key);
              }}>
              知道了
            </PosButton>
          </>
        ),
      });
    });

    const id2 = addWsListener<{ username: string; displayName: string }>(
      DOMAIN_INTERACT_OUTSIDE,
      WsActions.WEAPP_USER_VISIT_IN_POI,
      payload => {
        if (!payload || !payload.username) return;
        const key = `global-notification-user-visit-weapp-${payload.username}`;
        if (notificationKeys.current.has(key)) {
          // 防止同一个用户的重复通知
          return;
        }
        notificationKeys.current.add(key);
        playSound('notification');
        notification.info({
          onClose: () => {
            notificationKeys.current.delete(key);
          },
          duration: null,
          message: `门店小程序到访通知`,
          description: (
            <>
              <div>客户 {payload.displayName} 访问了小程序</div>
            </>
          ),
          key,
          actions: (
            <Space>
              <PosButton
                size={'large'}
                onClick={() => {
                  notificationKeys.current.delete(key);
                  notification.destroy(key);
                }}>
                忽略
              </PosButton>
              <PosButton
                size={'large'}
                type={'primary'}
                onClick={() => {
                  notificationKeys.current.delete(key);
                  notification.destroy(key);
                  helpers.showUserModal({
                    userName: payload.username,
                  });
                }}>
                查看
              </PosButton>
            </Space>
          ),
        });
      }
    );
    return () => {
      removeWsListener(id);
      removeWsListener(id2);
    };
  }, [notificationKeys]);

  // 快捷键
  // 开钱箱
  bindOpenCashDraw(openCashDraw);
  // 回到收银台
  bindGotoWorkbench(() => {
    navigate(`/POS/checkout`);
  });
  bindGotoLauncher(() => {
    navigate(`/POS/index`);
  });
  bindGotoOrder(() => {
    navigate(`/POS/order`);
  });
  bindGotoUser(() => {
    navigate(`/POS/user`);
  });
  bindGotoSetting(() => {
    navigate(`/POS/system`);
  });
  // 刷新
  bindReload(() => {
    location.href = `/POS/checkout`;
  });

  // 心跳检测
  useEffect(() => {
    if (requireLogin === RequireLoginMode.RequiredAdminChanged) {
      setTimeout(() => {
        window.location.reload();
      }, 3000);
      return;
    }

    heartbeatInterval.current = setInterval(() => {
      if (requireLogin === RequireLoginMode.Never) {
        if (!IS_PROD) {
          clearInterval(heartbeatInterval.current);
          return;
        }
        api
          .get({ url: `/baseInfo/heartbeat` })
          .then()
          .catch(e => {
            if (e && e.http && e.http.status === 401) {
              clearInterval(heartbeatInterval.current);
              setRequireLogin(RequireLoginMode.Required);
              terminateWsConnection();
            }
          });
      }
    }, 10e3);
    return () => {
      clearInterval(heartbeatInterval.current);
    };
  }, [requireLogin]);

  return (
    <>
      {kickAwayReason ? <KickAwayModal reason={kickAwayReason} /> : null}
      <Modal
        destroyOnHidden
        open={requireLogin !== RequireLoginMode.Never}
        closable={false}
        closeIcon={null}
        style={{ background: 'transparent', overflow: 'hidden', borderRadius: 10, padding: 0 }}
        styles={{ body: { padding: 0 } }}
        footer={null}>
        {requireLogin === RequireLoginMode.Required && (
          <Login
            mode={'modal'}
            onSuccess={loginInfo => {
              message.success(`登录成功`);
              if (loginInfo.userName !== userName) {
                // 更换了登录用户
                setRequireLogin(RequireLoginMode.RequiredAdminChanged);
              } else {
                setRequireLogin(RequireLoginMode.Never);
                restartWsConnection();
              }
            }}
          />
        )}
        {requireLogin === RequireLoginMode.RequiredAdminChanged && (
          <Alert
            message={<>登录帐号更改，即将重新进入系统</>}
            type={'warning'}
            style={{ textAlign: 'center' }}
          />
        )}
      </Modal>

      <Layout className={styles.content}>
        <LayoutPosHeader />
        {/* <ConfigProvider componentSize={'large'}> */}
        <POSConfigContext.Provider
          value={{
            config: posConfig,
            updateConfig: updatePosConfig,
          }}>
          <Synchronizer>
            {notificationNode}
            <Content className={styles.children}>{children}</Content>
          </Synchronizer>
        </POSConfigContext.Provider>
        {/* </ConfigProvider> */}
      </Layout>
    </>
  );
};

export default LayoutPos;
